import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { setUser } from '../../store/userSlice'; // Import the action

import axios from 'axios';
import config from '../../config';

import '../../styles/login.css';
import '../../styles/popup.css';

import IconButton from '@mui/material/IconButton';
import OutlinedInput from '@mui/material/OutlinedInput';
import InputLabel from '@mui/material/InputLabel';
import InputAdornment from '@mui/material/InputAdornment';
import FormControl from '@mui/material/FormControl';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import TextField from '@mui/material/TextField';

import { cacheProfilePicture } from '../../store/cacheProfile'

const API_URL = config.API_URL || 'http://localhost:5000';

const Login = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch(); // Initialize Redux dispatch

    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [showPassword, setShowPassword] = React.useState(false);
    const [isOverlayOpen, setIsOverlayOpen] = useState(false);

    const handleClickShowPassword = () => setShowPassword((show) => !show);

    const handleMouseDownPassword = (event) => {
        event.preventDefault();
    };

    const handleMouseUpPassword = (event) => {
        event.preventDefault();
    };

    const handleLogin = async (event) => {
        event.preventDefault();
        setErrorMessage('');
        setIsLoading(true);

        try {
            const response = await axios.post(
                `${API_URL}/v2/auth/signin`,
                { email, password },
                {
                    headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}`, 'Content-Type': 'application/json' }
                }
            );

            const data = response.data;

            if (response.status === 200 || response.status === 201) {
                // Dispatch user data to Redux store
                dispatch(setUser({
                    id: data.id,
                    email: data.email,
                    accounts: data.accounts,
                    active: data.active,
                    name: `${data.givenName} ${data.lastName}`.trim(),
                    profilePicture: data.profilePicture
                }));

                localStorage.setItem('accessToken', data.accessToken);
                localStorage.setItem('refreshToken', data.refreshToken);

                if (data.profilePicture?.key) {
                    cacheProfilePicture(data.profilePicture.key, data.id);
                }
                // Redirect to the dashboard or another page
                navigate('/dashboard');
            } else {
                setErrorMessage(data.error || 'Login failed');
            }
        } catch (error) {
            console.error('Login error:', error);
            // Check if it's an authentication error (401 or 403)
            if (error.response?.status === 401 || error.response?.status === 403) {
                setErrorMessage('Incorrect email or password. Please try again.');
            }
            // Network or unexpected errors
            else if (error.message === 'Network Error') {
                setErrorMessage('Unable to connect to the server. Please check your internet connection.');
            }
            else {
                setErrorMessage('An unexpected error occurred. Please try again.');
            }
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="login-container">
            <>
                <div className="w-full flex items-center justify-center mb-[50px] sm:hidden">
                    <img
                        src="/alt-logo/logo-icon.png"
                        className="login-logo w-[40px] mt-[40px] inline-block"
                        alt="small logo"
                    />
                </div>
                <img
                    src="/alt-logo/yellow.png"
                    className="login-logo w-[200px] m-[25px] hidden sm:block"
                    alt="large logo"
                />
            </>
            <div className="login-form-container h-[65%] lg:h-[85%]">
                <div className="login-form w-[80%] lg:w-[60%] xl:w-[50%]">
                    <h1 class="text-2xl mb-[40px] lg:text-3xl">Welcome back!</h1>
                    <TextField
                        fullWidth
                        id="email"
                        label="Email"
                        variant="outlined"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                    />
                    <div className="mt-[20px] md:mt-[30px]">
                        <FormControl sx={{ width: "100%" }} variant="outlined">
                            <InputLabel htmlFor="outlined-adornment-password">Password</InputLabel>
                            <OutlinedInput
                                id="password"
                                type={showPassword ? 'text' : 'password'}
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                endAdornment={
                                    <InputAdornment position="end">
                                        <IconButton
                                            aria-label={
                                                showPassword ? 'hide the password' : 'display the password'
                                            }
                                            onClick={handleClickShowPassword}
                                            onMouseDown={handleMouseDownPassword}
                                            onMouseUp={handleMouseUpPassword}
                                            edge="end"
                                        >
                                            {showPassword ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                    </InputAdornment>
                                }
                                label="Password"
                            />
                        </FormControl>
                    </div>
                    <p className="forgot-password" onClick={() => setIsOverlayOpen(true)}>Forgot password</p>
                    <button
                        className="login-button mt-[50px] lg:mt-[70px]"
                        onClick={handleLogin}
                        disabled={isLoading || !email || !password}
                    >
                        {isLoading ? 'Logging in...' : 'Log In'}
                    </button>
                    {errorMessage && <div className="error-message">{errorMessage}</div>}
                    <div className="w-full">
                        {/* Large */}
                        <p className="hidden lg:block text-md ">
                            Don’t have an account? <span className="sign-up">Sign Up</span>
                        </p>
                        {/* Small */}
                        <div className="block lg:hidden text-center text-sm mt-[30px]">
                            <p>Don’t have an account?</p>
                            <p className="sign-up">Sign Up</p>
                        </div>
                    </div>
                </div>
            </div>
            {isOverlayOpen && (
                <div className="popup-overlay">
                    <div className="popup-content">
                        <button className="close-popup" onClick={() => setIsOverlayOpen(false)}>
                            <i className="fas fa-times"></i>
                        </button>
                        <h2 className="text-xl md:text-2xl">Forgot your password?</h2>
                        <p className="popup-msg" style={{ marginBottom: "15px" }}>Enter your email address to reset it.</p>
                        <TextField
                            fullWidth
                            id="email"
                            label="Email"
                            variant="outlined"
                        />
                        <button className="login-button" style={{ marginTop: '20px' }}>Send Reset Link</button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Login;
